import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ShoppingCart } from "lucide-react";
import { toast } from "sonner";
import { useCart } from "@/context/CartContext";
import { useShop } from "@/context/ShopContext";
import { AppButton, Chip, Footer, ScreenHeader, Segmented, Stepper } from "@/components/AppUI";
import { SIZE_ICONS } from "@/pages/ProductPage";
import { SIZE_LABELS, euro, ingredientsOf, priceFor } from "@/lib/pizzeria";

const MAX_GUSTI = { rotonda: 1, mezza: 2, teglia: 3 };
const SIZES = ["rotonda", "teglia", "mezza"];
const newGusto = () => ({ base: "rossa", mozzarella: true, mode: "ingr", pizzaId: null, ingredients: [], removed: [] });
const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-");

const Q = ({ children }) => <p className="text-[13px] font-medium mt-1" style={{ color: "var(--muted-c)" }}>{children}</p>;

const perIngr = (cfg, size) => cfg[`per_ingredient_${size}`] ?? cfg.per_ingredient ?? 0;

const gustoPrice = (g, size, cfg, pizzas, featured) => {
  if (g.mode === "menu") {
    const p = pizzas.find((x) => x.id === g.pizzaId);
    return p ? priceFor(p, size, featured) : 0;
  }
  return (cfg[`base_${size}`] || 0) + g.ingredients.length * perIngr(cfg, size);
};

const describe = (g, pizzas, extras) => {
  if (g.mode === "menu") {
    const p = pizzas.find((x) => x.id === g.pizzaId);
    return `${p?.name || ""}${g.removed.length ? ` (senza ${g.removed.join(", ")})` : ""}`;
  }
  const names = extras.filter((x) => g.ingredients.includes(x.id)).map((x) => x.name);
  return `${g.base === "bianca" ? "Bianca" : "Rossa"}, ${g.mozzarella ? "con" : "senza"} mozzarella – ${names.join(", ") || "solo base"}`;
};

const GustoCard = ({ i, total, g, set, size, cfg, pizzas, extras, featured }) => {
  const max = cfg.max_ingredients || 4;
  const pizza = pizzas.find((x) => x.id === g.pizzaId);
  const toggle = (k, v) => set({ [k]: g[k].includes(v) ? g[k].filter((x) => x !== v) : [...g[k], v] });
  return (
    <div className="rounded-2xl p-4 space-y-2" style={{ background: "var(--surface-2)", border: "1px solid var(--border-c)" }} data-testid={`builder-gusto-${i}`}>
      <p className="text-base font-bold">{total === 1 ? "Il tuo gusto" : `Gusto ${i + 1}`}</p>
      <Q>Come la vuoi?</Q>
      <Segmented testIdPrefix={`builder-mode-${i}`} value={g.mode} onChange={(mode) => set({ mode })} options={[{ key: "ingr", label: "La creo io" }, { key: "menu", label: "Gusto del menù" }]} />
      <Q>Bianca o rossa?</Q>
      <Segmented testIdPrefix={`builder-base-${i}`} value={g.base} onChange={(base) => set({ base })} options={[{ key: "rossa", label: "Rossa" }, { key: "bianca", label: "Bianca" }]} />
      <Q>Mozzarella?</Q>
      <Segmented testIdPrefix={`builder-mozz-${i}`} value={g.mozzarella ? "si" : "no"} onChange={(v) => set({ mozzarella: v === "si" })} options={[{ key: "si", label: "Con mozzarella" }, { key: "no", label: "Senza" }]} />
      {g.mode === "ingr" ? (
        <>
          <Q>Ingredienti</Q>
          <p className="text-[13px]" style={{ color: "var(--muted-c)" }} data-testid={`builder-ingr-count-${i}`}>{g.ingredients.length}/{max} ingredienti · +{euro(perIngr(cfg, size))} ciascuno</p>
          <div className="flex flex-wrap gap-2">
            {extras.map((x) => {
              const on = g.ingredients.includes(x.id);
              return <Chip key={x.id} on={on} disabled={!on && g.ingredients.length >= max} onClick={() => toggle("ingredients", x.id)} testId={`builder-ingr-${i}-${x.id}`}>{x.name}</Chip>;
            })}
          </div>
        </>
      ) : (
        <>
          <Q>Scegli la pizza del menù</Q>
          <div className="flex flex-wrap gap-2">
            {pizzas.map((p) => <Chip key={p.id} on={g.pizzaId === p.id} onClick={() => set({ pizzaId: p.id, removed: [] })} testId={`builder-pizza-${i}-${p.id}`}>{p.name} · {euro(priceFor(p, size, featured))}</Chip>)}
          </div>
          {pizza && ingredientsOf(pizza).length > 0 && (
            <div className="w-full space-y-1.5">
              <Q>Vuoi togliere qualcosa dalla {pizza.name}?</Q>
              <div className="flex flex-wrap gap-2">
                {ingredientsOf(pizza).map((x) => <Chip key={x} on={g.removed.includes(x)} strike={g.removed.includes(x)} onClick={() => toggle("removed", x)} testId={`builder-remove-${i}-${slug(x)}`}>{g.removed.includes(x) ? `senza ${x}` : x}</Chip>)}
              </div>
            </div>
          )}
        </>
      )}
      <p className="text-right text-[15px] font-medium" style={{ color: "var(--brand)" }} data-testid={`builder-gusto-price-${i}`}>{euro(gustoPrice(g, size, cfg, pizzas, featured))}</p>
    </div>
  );
};

export default function BuilderPage() {
  const nav = useNavigate();
  const { add } = useCart();
  const { settings, products, deals: featured } = useShop();
  const cfg = settings?.builder || {};
  const [size, setSize] = useState("rotonda");
  const [gusti, setGusti] = useState([newGusto()]);
  const [qty, setQty] = useState(1);
  const [error, setError] = useState("");

  if (settings && cfg.enabled === false) {
    return <><ScreenHeader title="Pizza a modo tuo" /><p className="p-6" style={{ color: "var(--muted-c)" }}>La pizza a modo tuo non è disponibile al momento.</p></>;
  }

  const pizzas = (products || []).filter((p) => p.sized && p[`price_${size}`] != null);
  const extras = (settings?.extras || []).filter((x) => x.available !== false);
  const max = MAX_GUSTI[size];
  const unit = Math.max(...gusti.map((g) => gustoPrice(g, size, cfg, pizzas, featured)));
  const pickSize = (s) => {
    setSize(s);
    setGusti((prev) => prev.slice(0, MAX_GUSTI[s]));
  };
  const setCount = (n) => setGusti((prev) => Array.from({ length: n }, (_, k) => prev[k] || newGusto()));
  const setGusto = (i, patch) => {
    setError("");
    setGusti((prev) => prev.map((g, k) => (k === i ? { ...g, ...patch } : g)));
  };

  const addToCart = () => {
    const missing = gusti.findIndex((g) => g.mode === "menu" && !g.pizzaId);
    if (missing >= 0) return setError(`Scegli la pizza del menù per il gusto ${missing + 1}`);
    setError("");
    const desc = gusti.map((g, i) => `${gusti.length > 1 ? `Gusto ${i + 1}: ` : ""}${describe(g, pizzas, extras)}`).join(" | ");
    const builder = { gusti: gusti.map((g) => ({ base: g.base, mozzarella: g.mozzarella, pizza_id: g.mode === "menu" ? g.pizzaId : null, ingredient_ids: g.mode === "ingr" ? g.ingredients : [], removed: g.mode === "menu" ? g.removed : [] })) };
    add({ product_id: "builder", name: "Pizza a modo tuo", size, quantity: qty, unit_price: Math.max(...gusti.map((g) => gustoPrice(g, size, cfg, pizzas, { featured: featured?.featured }))), extras: [], removed: [], image: "", ingredients: desc, builder });
    toast.success("Pizza a modo tuo aggiunta al carrello");
    nav(-1);
  };

  return (
    <div className="flex-1 flex flex-col" data-testid="builder-page">
      <ScreenHeader title="Pizza a modo tuo" />
      <div className="flex-1 p-4 pb-8 space-y-3">
        <p className="text-lg font-medium mt-1">1. Che formato?</p>
        <div className="flex gap-2">
          {SIZES.map((s) => {
            const Icon = SIZE_ICONS[s];
            const on = size === s;
            return (
              <button key={s} onClick={() => pickSize(s)} data-testid={`builder-size-${s}`} className="press flex-1 p-3 rounded-2xl flex flex-col items-center gap-1"
                style={{ border: `1px solid ${on ? "var(--brand)" : "var(--border-c)"}`, background: on ? "var(--brand)" : "var(--surface-2)", color: on ? "var(--on-brand)" : "var(--on-surface)" }}>
                <Icon className="h-6 w-6" />
                <span className="text-sm font-medium truncate max-w-full">{SIZE_LABELS[s]}</span>
                <span className="text-[11px]" style={{ opacity: on ? 1 : 0.7 }}>{MAX_GUSTI[s] === 1 ? "1 gusto" : `fino a ${MAX_GUSTI[s]} gusti`}</span>
              </button>
            );
          })}
        </div>
        <p className="text-lg font-medium mt-1">2. Quanti gusti?</p>
        <Segmented testIdPrefix="builder-gusti" value={String(gusti.length)} onChange={(v) => setCount(Number(v))} disabled={["1", "2", "3"].filter((k) => Number(k) > max)}
          options={[1, 2, 3].map((n) => ({ key: String(n), label: `${n} ${n === 1 ? "gusto" : "gusti"}` }))} />
        {max < 3 && <p className="text-[13px]" style={{ color: "var(--muted-c)" }} data-testid="builder-gusti-hint">Con la {SIZE_LABELS[size].toLowerCase()} puoi scegliere {max === 1 ? "1 gusto" : `fino a ${max} gusti`}. Per 3 gusti scegli la teglia.</p>}
        {gusti.map((g, i) => <GustoCard key={i} i={i} total={gusti.length} g={g} set={(patch) => setGusto(i, patch)} size={size} cfg={cfg} pizzas={pizzas} extras={extras} featured={featured} />)}
        {gusti.length > 1 && <p className="text-sm" style={{ color: "var(--muted-c)" }}>Con più gusti si paga il gusto più caro.</p>}
        {error && <p className="text-sm" style={{ color: "var(--brand)" }} data-testid="builder-error">{error}</p>}
      </div>
      <Footer>
        <div className="flex items-center gap-3">
          <Stepper value={qty} onChange={(v) => setQty(Math.max(1, v))} testIdPrefix="builder-qty" />
          <AppButton title={`Aggiungi · ${euro(unit * qty)}`} icon={ShoppingCart} onClick={addToCart} testId="builder-add-button" className="flex-1" />
        </div>
      </Footer>
    </div>
  );
}
